import React, { useRef, useEffect } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  color: string;
}

interface CanvasParticlesProps {
  count?: number;
  colors?: string[];
  speedMultiplier?: number;
  className?: string;
}

export const CanvasParticles: React.FC<CanvasParticlesProps> = ({
  count = 75,
  colors = ['rgba(251, 191, 36, 0.6)', 'rgba(239, 68, 68, 0.5)'],
  speedMultiplier = 1,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationIdRef = useRef<number>();

  // Initialize particle with random properties
  const createParticle = (canvas: HTMLCanvasElement, startAtTop = false): Particle => {
    const x = Math.random() * canvas.width;
    const y = startAtTop 
      ? -10 
      : Math.random() * (canvas.height + 20) - 10; // Distribute across screen + buffer
    
    return {
      x,
      y,
      vx: (Math.random() - 0.5) * 0.3 * speedMultiplier, // Horizontal drift
      vy: -(Math.random() * 1.5 + 0.5) * speedMultiplier, // Upward speed
      size: Math.random() * 2.5 + 0.5,
      opacity: Math.random() * 0.5 + 0.3,
      color: colors[Math.floor(Math.random() * colors.length)],
    };
  };

  // Initialize all particles
  const initParticles = (canvas: HTMLCanvasElement) => {
    particlesRef.current = [];
    for (let i = 0; i < count; i++) {
      // Start with particles distributed across the screen
      particlesRef.current.push(createParticle(canvas, false));
    }
  };

  // Update particle positions
  const updateParticles = (canvas: HTMLCanvasElement) => {
    particlesRef.current.forEach((particle) => {
      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;

      // Reset particle if it goes off screen
      if (particle.y < -10) {
        // Reset at bottom with some randomness
        particle.y = canvas.height + 10;
        particle.x = Math.random() * canvas.width;
        particle.vx = (Math.random() - 0.5) * 0.3 * speedMultiplier;
      }

      // Wrap horizontally
      if (particle.x < -10) particle.x = canvas.width + 10;
      if (particle.x > canvas.width + 10) particle.x = -10;
    });
  };

  // Render all particles
  const renderParticles = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw each particle
    particlesRef.current.forEach((particle) => {
      ctx.globalAlpha = particle.opacity;
      ctx.fillStyle = particle.color;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.globalAlpha = 1;
  };

  // Animation loop
  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    updateParticles(canvas);
    renderParticles(ctx, canvas);

    animationIdRef.current = requestAnimationFrame(animate);
  };

  // Handle resize
  const handleResize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size to window size
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
    }

    // Reinitialize particles for new canvas size
    initParticles(canvas);
  };

  // Setup and cleanup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Initial setup
    handleResize();
    
    // Start animation
    animate();

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [count, speedMultiplier]);

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 pointer-events-none ${className}`}
      style={{
        width: '100%',
        height: '100%',
        filter: 'blur(0.5px)',
      }}
    />
  );
};